/**
 * หน้าจอความล้มเหลวของคิวข้อขัดแย้ง — หนึ่งสถานะ หนึ่งข้อความ
 *
 * กรณีที่สำคัญที่สุดคือ **`409 CONFLICT_ALREADY_RESOLVED`** และมันต้องอ่านออกว่า
 * "มีคนอื่นตัดสินไปก่อนแล้ว" ไม่ใช่ "ระบบผิดพลาด": conflict ที่ปิดแล้วบันทึกไว้ว่า
 * ใครตัดสินและเมื่อไร การตัดสินครั้งที่สองจะทับครั้งแรกทิ้ง เซิร์ฟเวอร์จึงไม่เขียน
 * อะไรเลย (D-077) — สิ่งที่ผู้ใช้ต้องรู้คือคิวเพิ่งถูกโหลดใหม่ และแถวนั้นไม่ใช่งาน
 * ของเขาอีกแล้ว ไม่ใช่ว่าเขาทำอะไรพัง
 *
 * `404` ก็กลืนสองความหมายไว้โดยตั้งใจฝั่งเซิร์ฟเวอร์เช่นกัน — conflict ที่ไม่มีอยู่
 * จริง กับ conflict ของอุปกรณ์ในโรงงานที่ผู้เรียกมองไม่เห็น (D-004)
 */

import { StatusBadge } from '../StatusBadge';
import type {
  ApiErrorEnvelope,
  OfflineConflictFailure,
} from '../../lib/offlineConflicts/client';
import {
  CONFLICT_ALREADY_RESOLVED,
  SHOP_FLOOR_MODULE,
} from '../../lib/offlineConflicts/types';

export interface OfflineConflictFailureNoticeProps {
  readonly failure: OfflineConflictFailure;
  /** `list` = โหลดคิวไม่สำเร็จ, `decision` = บันทึกการตัดสินไม่สำเร็จ */
  readonly context: 'list' | 'decision';
  readonly testId: string;
  readonly onRetry?: () => void;
}

interface FailureCopy {
  readonly heading: string;
  readonly text: string;
}

function envelopeOf(failure: OfflineConflictFailure): ApiErrorEnvelope | null {
  return 'error' in failure ? failure.error : null;
}

/**
 * ข้อความประจำแต่ละสถานะ — แจกแจงครบโดยไม่มี `default` เพื่อให้ TypeScript เป็น
 * ผู้พิสูจน์ว่าไม่มีสถานะไหนตกหล่นเมื่อ client เพิ่มกรณีใหม่
 */
function failureCopy(failure: OfflineConflictFailure, context: 'list' | 'decision'): FailureCopy {
  switch (failure.status) {
    case 'unauthenticated':
      return {
        heading: 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้',
        text: 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว token ที่ถืออยู่จึงถูกล้างทิ้งและต้องเข้าสู่ระบบใหม่ (D-049)',
      };
    case 'forbidden':
      return {
        heading:
          context === 'decision'
            ? 'สิทธิ์ของคุณยังบันทึกการตัดสินไม่ได้'
            : 'สิทธิ์ของคุณไม่ครอบคลุมการดูคิวนี้',
        text:
          context === 'decision'
            ? `เซิร์ฟเวอร์ตอบ 403 — การปิดข้อขัดแย้งต้องใช้โมดูล ${SHOP_FLOOR_MODULE} และสิทธิ์อนุมัติ (D-077) ขอสิทธิ์กับผู้ดูแลระบบ การกดซ้ำจะไม่ช่วยอะไร`
            : `เซิร์ฟเวอร์ตอบ 403 — คิวนี้ถูก gate ด้วยโมดูล ${SHOP_FLOOR_MODULE} และสิทธิ์ดู ขอสิทธิ์กับผู้ดูแลระบบ`,
      };
    case 'not-found':
      return {
        heading: 'ไม่พบข้อขัดแย้งใบนี้ในมุมมองของคุณ',
        text: 'เซิร์ฟเวอร์ตอบ 404 — ระบบไม่ยืนยันว่ารายการนี้ไม่มีอยู่จริง หรือมีอยู่แต่มาจากอุปกรณ์ในโรงงานที่คุณไม่มีสิทธิ์เห็น สองอย่างนี้ตอบเหมือนกันโดยตั้งใจ (D-004) — คิวถูกโหลดใหม่แล้ว',
      };
    case 'conflict':
      return {
        heading: 'มีคนตัดสินรายการนี้ไปก่อนแล้ว',
        text: `เซิร์ฟเวอร์ตอบ 409 ${CONFLICT_ALREADY_RESOLVED} และไม่ได้เขียนอะไรเลย — การตัดสินที่บันทึกไว้แล้วระบุว่าใครเป็นผู้ตัดสินและเมื่อไร การตัดสินครั้งที่สองจะทับของเดิมทิ้ง (D-077) คิวถูกโหลดใหม่ให้แล้ว ดูผลที่บันทึกไว้ได้ที่มุมมอง "ตัดสินแล้ว"`,
      };
    case 'bad-request':
      return {
        heading: 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง',
        text: 'เซิร์ฟเวอร์ตอบ 400 — รูปแบบของรหัสข้อขัดแย้งหรือของ cursor หน้าถัดไปไม่ตรงกับที่ API รับ ลองโหลดคิวใหม่แทนการกดซ้ำ',
      };
    case 'server-error':
      return {
        heading: `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`,
        text:
          context === 'decision'
            ? 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ — ยังไม่มีการตัดสินใดถูกบันทึก และรายการนี้ยังค้างอยู่ในคิวตามเดิม'
            : 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ ยังไม่มีคิวจริงมาแสดง — ห้ามตีความว่าไม่มีคำสั่งค้างรอคนตัดสิน',
      };
    case 'network-error':
      return {
        heading: 'ติดต่อเซิร์ฟเวอร์ไม่ได้',
        text:
          context === 'decision'
            ? `${failure.detail} — การตัดสินยังไม่ถูกบันทึก รายการนี้จึงยังค้างอยู่ในคิวตามเดิม`
            : `${failure.detail} — หน้าจอนี้จึงไม่มีคิวจะแสดง และจะไม่เดาข้อมูลขึ้นมาเอง`,
      };
    case 'invalid-response':
      return {
        heading: 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ',
        text: 'หน้าจอปฏิเสธที่จะแสดงคิวที่อ่านได้ไม่ครบ ดีกว่าแสดงรายการที่ขาดไปบางบรรทัด แล้วมีคนสรุปว่าไม่เหลืองานค้างทั้งที่ยังมีคำสั่งหน้างานรอคนตัดสินอยู่',
      };
    case 'cancelled':
      return {
        heading: 'ยกเลิกการเรียกข้อมูลแล้ว',
        text: 'คำขอถูกยกเลิกก่อนได้รับคำตอบ',
      };
  }
}

export function OfflineConflictFailureNotice({
  failure,
  context,
  testId,
  onRetry,
}: OfflineConflictFailureNoticeProps) {
  const copy = failureCopy(failure, context);
  const envelope = envelopeOf(failure);

  return (
    <div className="cap-notice" data-testid={testId} data-failure={failure.status} role="status">
      <StatusBadge tone={failure.status === 'conflict' ? 'warning' : 'danger'}>
        {context === 'decision' ? 'บันทึกการตัดสินไม่สำเร็จ' : 'อ่านข้อมูลไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{copy.heading}</h3>
      <p className="cap-notice__text">{copy.text}</p>

      {envelope?.rule === null || envelope === null ? null : (
        <p className="cap-notice__note" data-testid={`${testId}-rule`}>
          กฎที่เซิร์ฟเวอร์อ้าง: <code className="cap-table__code">{envelope.rule}</code>
        </p>
      )}

      {envelope?.message === null || envelope === null ? null : (
        <p className="cap-notice__note" data-testid={`${testId}-message`}>
          ข้อความจากเซิร์ฟเวอร์: {envelope.message}
        </p>
      )}

      {onRetry === undefined ? null : (
        <div className="cap-notice__actions">
          <button type="button" className="cap-button cap-button--ghost" onClick={onRetry}>
            ลองอีกครั้ง
          </button>
        </div>
      )}
    </div>
  );
}
